<template>
  <div class="chat-history">
    <NavBar />
    <AuthBySessionExpired />

    <div class="chat-history__container">
      <!-- ── Cabeçalho (mesmo padrão da tela de Plantões) ─────────────── -->
      <header class="chat-history__header">
        <div class="chat-history__header-content">
          <v-btn class="btn-return" @click="handleToBack">
            <i class="fas fa-arrow-left mr-2" aria-hidden="true"></i>
            Voltar
          </v-btn>

          <div>
            <h1 class="chat-history__title">
              <i
                class="fas fa-comments chat-history__title-icon"
                aria-hidden="true"
              ></i>
              Mensagens da equipe
            </h1>
            <p class="chat-history__subtitle">
              Histórico de mensagens recebidas e enviadas, com confirmação de
              leitura
            </p>
          </div>

          <button
            type="button"
            class="ch-btn ch-btn--primary"
            :disabled="!online"
            @click="newMessage"
          >
            <i class="fas fa-pen" aria-hidden="true"></i>
            Nova mensagem
          </button>
        </div>
      </header>

      <!-- ── Offline ─────────────────────────────────────────────────── -->
      <div v-if="!online" class="chat-history__offline" role="status">
        <i class="fas fa-wifi" aria-hidden="true"></i>
        O histórico de mensagens precisa de conexão com a internet.
      </div>

      <template v-else>
        <!-- ── Abas ──────────────────────────────────────────────────── -->
        <div
          class="chat-history__tabs"
          role="tablist"
          aria-label="Tipo de mensagem"
          @keydown.right.prevent="switchTabByKey"
          @keydown.left.prevent="switchTabByKey"
        >
          <button
            v-for="tab in tabs"
            :id="`chat-tab-${tab.value}`"
            :key="tab.value"
            ref="tabButtons"
            type="button"
            role="tab"
            :aria-selected="box === tab.value ? 'true' : 'false'"
            aria-controls="chat-history-panel"
            :tabindex="box === tab.value ? 0 : -1"
            :class="[
              'chat-history__tab',
              { 'chat-history__tab--active': box === tab.value },
            ]"
            @click="setBox(tab.value)"
          >
            <i :class="['fas', tab.icon]" aria-hidden="true"></i>
            {{ tab.label }}
            <span
              v-if="tab.value === 'RECEIVED' && unreadTotal > 0"
              class="chat-history__tab-badge"
            >
              {{ unreadTotal }}
              <span class="ch-sr">não lidas</span>
            </span>
          </button>
        </div>

        <!-- ── Filtros ──────────────────────────────────────────────── -->
        <section class="chat-history__filters" aria-label="Filtros">
          <div class="chat-history__filter chat-history__filter--search">
            <label for="ch-filter-search" class="chat-history__filter-label">
              <i class="fas fa-search" aria-hidden="true"></i>
              Buscar
            </label>
            <input
              id="ch-filter-search"
              v-model="searchInput"
              type="search"
              class="ch-input"
              :placeholder="
                box === 'RECEIVED'
                  ? 'Texto ou nome de quem enviou'
                  : 'Texto da mensagem'
              "
              autocomplete="off"
            />
          </div>

          <div class="chat-history__filter">
            <label for="ch-filter-person" class="chat-history__filter-label">
              <i class="fas fa-user" aria-hidden="true"></i>
              {{ box === "RECEIVED" ? "Enviada por" : "Enviada para" }}
            </label>
            <select
              id="ch-filter-person"
              v-model="filters.person_id"
              class="ch-input"
            >
              <option value="">Todas as pessoas</option>
              <option
                v-for="user in chat.recipients"
                :key="user.id"
                :value="String(user.id)"
              >
                {{ user.name }}
              </option>
            </select>
          </div>

          <div class="chat-history__filter">
            <label for="ch-filter-status" class="chat-history__filter-label">
              <i class="fas fa-check-double" aria-hidden="true"></i>
              Leitura
            </label>
            <select
              id="ch-filter-status"
              v-model="filters.status"
              class="ch-input"
            >
              <option value="">Todas</option>
              <option v-if="box === 'RECEIVED'" value="UNREAD">
                Somente não lidas
              </option>
              <option v-else value="PENDING">Alguém ainda não leu</option>
            </select>
          </div>

          <div class="chat-history__filter">
            <label for="ch-filter-init" class="chat-history__filter-label">
              <i class="fas fa-calendar-alt" aria-hidden="true"></i>
              De
            </label>
            <input
              id="ch-filter-init"
              v-model="filters.date_init"
              type="date"
              class="ch-input"
              :max="filters.date_end || undefined"
            />
          </div>

          <div class="chat-history__filter">
            <label for="ch-filter-end" class="chat-history__filter-label">
              <i class="fas fa-calendar-check" aria-hidden="true"></i>
              Até
            </label>
            <input
              id="ch-filter-end"
              v-model="filters.date_end"
              type="date"
              class="ch-input"
              :min="filters.date_init || undefined"
            />
          </div>
        </section>

        <!-- ── Resumo do resultado ──────────────────────────────────── -->
        <div class="chat-history__summary">
          <p class="chat-history__count" role="status" aria-live="polite">
            <template v-if="!loading">
              {{ total === 1 ? "1 mensagem" : `${total} mensagens` }}
              {{ hasFilters ? "com esses filtros" : "" }}
            </template>
          </p>
          <button
            v-if="hasFilters"
            type="button"
            class="ch-link-btn"
            @click="clearFilters"
          >
            <i class="fas fa-times" aria-hidden="true"></i>
            Limpar filtros
          </button>
        </div>

        <ErrorsMessages :errors="errors" />

        <!-- ── Lista ─────────────────────────────────────────────────── -->
        <b-overlay :show="loading" opacity="0.7" rounded="lg">
          <div
            id="chat-history-panel"
            ref="panel"
            role="tabpanel"
            :aria-labelledby="`chat-tab-${box}`"
            class="chat-history__panel"
          >
            <!-- Vazio -->
            <div
              v-if="!loading && !messages.length"
              class="chat-history__empty"
            >
              <i class="far fa-comments" aria-hidden="true"></i>
              <p v-if="hasFilters">
                Nenhuma mensagem encontrada com esses filtros.
              </p>
              <p v-else-if="box === 'RECEIVED'">
                Você ainda não recebeu mensagens.
              </p>
              <p v-else>Você ainda não enviou mensagens.</p>
              <button
                v-if="hasFilters"
                type="button"
                class="ch-btn ch-btn--secondary"
                @click="clearFilters"
              >
                Limpar filtros
              </button>
              <button
                v-else
                type="button"
                class="ch-btn ch-btn--primary"
                @click="newMessage"
              >
                <i class="fas fa-pen" aria-hidden="true"></i>
                Escrever mensagem
              </button>
            </div>

            <!--
              Agrupado por dia: em turnos de 12h, "o que chegou hoje" é a
              primeira pergunta. O separador responde sem precisar ler datas.
            -->
            <section
              v-for="group in groupedMessages"
              :key="group.key"
              class="chat-history__day"
              :aria-label="group.label"
            >
              <h2 class="chat-history__day-label">{{ group.label }}</h2>

              <ol class="chat-history__list">
                <li
                  v-for="message in group.items"
                  :key="message.id"
                  :class="[
                    'ch-card',
                    {
                      'ch-card--unread': box === 'RECEIVED' && !message.read_at,
                    },
                  ]"
                >
                  <!-- Avatar com iniciais (reconhecimento rápido de quem) -->
                  <span class="ch-card__avatar" aria-hidden="true">
                    <i
                      v-if="box === 'SENT' && message.is_broadcast"
                      class="fas fa-users"
                    ></i>
                    <template v-else>{{
                      initials(cardPerson(message))
                    }}</template>
                  </span>

                  <div class="ch-card__content">
                    <!-- Linha 1: pessoa, destino, hora -->
                    <div class="ch-card__top">
                      <span v-if="box === 'RECEIVED'" class="ch-card__person">
                        {{ message.sender_name }}
                      </span>
                      <span v-else class="ch-card__person">
                        <span class="ch-card__to">Para:</span>
                        {{ sentToLabel(message) }}
                      </span>

                      <span
                        :class="[
                          'ch-tag',
                          message.is_broadcast ? 'ch-tag--all' : 'ch-tag--me',
                        ]"
                      >
                        <i
                          :class="[
                            'fas',
                            message.is_broadcast ? 'fa-users' : 'fa-user',
                          ]"
                          aria-hidden="true"
                        ></i>
                        {{ destinationTag(message) }}
                      </span>

                      <time
                        class="ch-card__time"
                        :datetime="message.created_at"
                      >
                        {{ formatTime(message.created_at) }}
                      </time>
                    </div>

                    <!-- Texto -->
                    <p
                      :class="[
                        'ch-card__body',
                        {
                          'ch-card__body--clamped':
                            isLong(message) && !expanded[message.id],
                        },
                      ]"
                    >
                      {{ message.body }}
                    </p>

                    <button
                      v-if="isLong(message)"
                      type="button"
                      class="ch-link-btn ch-link-btn--small"
                      :aria-expanded="expanded[message.id] ? 'true' : 'false'"
                      @click="toggleExpanded(message.id)"
                    >
                      {{
                        expanded[message.id]
                          ? "Ver menos"
                          : "Ver mensagem completa"
                      }}
                    </button>

                    <!-- Rodapé: status de leitura + ações -->
                    <div class="ch-card__bottom">
                      <span
                        v-if="message.origin === 'VOICE'"
                        class="ch-card__voice"
                        title="Mensagem gravada por voz e convertida em texto"
                      >
                        <i class="fas fa-microphone" aria-hidden="true"></i>
                        Transcrita de voz
                      </span>

                      <!-- RECEBIDAS: eu li? -->
                      <template v-if="box === 'RECEIVED'">
                        <span
                          v-if="message.read_at"
                          class="ch-status ch-status--read"
                        >
                          <i class="fas fa-check-double" aria-hidden="true"></i>
                          Lida {{ formatReadAt(message.read_at) }}
                        </span>
                        <span v-else class="ch-status ch-status--unread">
                          <i class="fas fa-circle" aria-hidden="true"></i>
                          Não lida
                        </span>

                        <span class="ch-card__actions">
                          <button
                            v-if="!message.read_at"
                            type="button"
                            class="ch-btn ch-btn--small ch-btn--secondary"
                            :disabled="markingId === message.id"
                            @click="markAsRead(message)"
                          >
                            <i
                              :class="[
                                'fas',
                                markingId === message.id
                                  ? 'fa-spinner fa-spin'
                                  : 'fa-check',
                              ]"
                              aria-hidden="true"
                            ></i>
                            Marcar como lida
                          </button>
                          <button
                            type="button"
                            class="ch-btn ch-btn--small ch-btn--secondary"
                            :aria-label="`Responder a ${message.sender_name}`"
                            @click="reply(message)"
                          >
                            <i class="fas fa-reply" aria-hidden="true"></i>
                            Responder
                          </button>
                        </span>
                      </template>

                      <!-- ENVIADAS: quem leu? -->
                      <template v-else>
                        <div class="ch-progress">
                          <span
                            :class="[
                              'ch-status',
                              `ch-status--${readState(message)}`,
                            ]"
                          >
                            <i
                              :class="['fas', readIcon(message)]"
                              aria-hidden="true"
                            ></i>
                            {{ readLabel(message) }}
                          </span>
                          <span
                            class="ch-progress__bar"
                            role="progressbar"
                            :aria-valuenow="message.recipients_read"
                            aria-valuemin="0"
                            :aria-valuemax="message.recipients_total"
                            :aria-label="readLabel(message)"
                          >
                            <span
                              :class="[
                                'ch-progress__fill',
                                `ch-progress__fill--${readState(message)}`,
                              ]"
                              :style="{ width: readPercent(message) + '%' }"
                            ></span>
                          </span>
                        </div>

                        <span class="ch-card__actions">
                          <button
                            type="button"
                            class="ch-btn ch-btn--small ch-btn--secondary"
                            @click="openReceipts(message)"
                          >
                            <i class="fas fa-eye" aria-hidden="true"></i>
                            Ver quem leu
                          </button>
                        </span>
                      </template>
                    </div>
                  </div>
                </li>
              </ol>
            </section>
          </div>
        </b-overlay>

        <!-- ── Paginação ────────────────────────────────────────────── -->
        <nav
          v-if="total > perPage"
          class="chat-history__pagination"
          aria-label="Páginas do histórico"
        >
          <b-pagination
            v-model="page"
            :total-rows="total"
            :per-page="perPage"
            align="center"
            first-number
            last-number
            label-first-page="Primeira página"
            label-prev-page="Página anterior"
            label-next-page="Próxima página"
            label-last-page="Última página"
            label-page="Ir para a página"
          />
        </nav>
      </template>
    </div>

    <!-- ═══ MODAL: QUEM LEU ═══════════════════════════════════════════ -->
    <b-modal
      id="chat-receipts"
      :visible="receiptsOpen"
      size="lg"
      centered
      scrollable
      title-tag="h2"
      @change="(v) => (receiptsOpen = v)"
    >
      <template #modal-title>
        <span class="ch-receipts__title">
          <i class="fas fa-check-double" aria-hidden="true"></i>
          Confirmação de leitura
        </span>
      </template>

      <b-overlay :show="receiptsLoading" opacity="0.7">
        <div class="ch-receipts">
          <template v-if="receipts">
            <!-- Mensagem de referência -->
            <blockquote class="ch-receipts__quote">
              <p>{{ excerpt(receipts.message.body, 220) }}</p>
              <footer>
                Enviada {{ formatReadAt(receipts.message.created_at) }} ·
                {{
                  receipts.message.is_broadcast
                    ? "para toda a equipe"
                    : `para ${receipts.total} ${
                        receipts.total === 1 ? "pessoa" : "pessoas"
                      }`
                }}
              </footer>
            </blockquote>

            <!-- Números grandes: resposta em 1 segundo -->
            <div class="ch-receipts__totals">
              <div class="ch-receipts__total ch-receipts__total--read">
                <strong>{{ receipts.read_count }}</strong>
                <span
                  ><i class="fas fa-check-double" aria-hidden="true"></i>
                  leram</span
                >
              </div>
              <div class="ch-receipts__total ch-receipts__total--pending">
                <strong>{{ receipts.total - receipts.read_count }}</strong>
                <span
                  ><i class="far fa-clock" aria-hidden="true"></i> ainda não
                  leram</span
                >
              </div>
            </div>

            <div v-if="receipts.total > 10" class="ch-receipts__search">
              <label for="ch-receipts-search" class="ch-sr"
                >Buscar pessoa</label
              >
              <i class="fas fa-search" aria-hidden="true"></i>
              <input
                id="ch-receipts-search"
                v-model="receiptsSearch"
                type="search"
                class="ch-input"
                placeholder="Buscar pessoa"
                autocomplete="off"
              />
            </div>

            <div class="ch-receipts__columns">
              <section
                class="ch-receipts__column"
                aria-labelledby="ch-receipts-pending"
              >
                <h3 id="ch-receipts-pending" class="ch-receipts__column-title">
                  <i class="far fa-clock" aria-hidden="true"></i>
                  Ainda não leram ({{ pendingRecipients.length }})
                </h3>
                <p v-if="!pendingRecipients.length" class="ch-receipts__none">
                  {{
                    receiptsSearch
                      ? "Ninguém com esse nome."
                      : "Todos já leram."
                  }}
                </p>
                <ul v-else class="ch-receipts__people">
                  <li
                    v-for="person in pendingRecipients"
                    :key="person.id"
                    class="ch-receipts__person"
                  >
                    <span class="ch-receipts__name">{{ person.name }}</span>
                    <span
                      v-if="person.user_group_name"
                      class="ch-receipts__group"
                    >
                      {{ person.user_group_name }}
                    </span>
                  </li>
                </ul>
              </section>

              <section
                class="ch-receipts__column"
                aria-labelledby="ch-receipts-read"
              >
                <h3 id="ch-receipts-read" class="ch-receipts__column-title">
                  <i class="fas fa-check-double" aria-hidden="true"></i>
                  Leram ({{ readRecipients.length }})
                </h3>
                <p v-if="!readRecipients.length" class="ch-receipts__none">
                  {{
                    receiptsSearch
                      ? "Ninguém com esse nome."
                      : "Ninguém leu ainda."
                  }}
                </p>
                <ul v-else class="ch-receipts__people">
                  <li
                    v-for="person in readRecipients"
                    :key="person.id"
                    class="ch-receipts__person"
                  >
                    <span class="ch-receipts__name">{{ person.name }}</span>
                    <span
                      v-if="person.user_group_name"
                      class="ch-receipts__group"
                    >
                      {{ person.user_group_name }}
                    </span>
                    <time class="ch-receipts__when" :datetime="person.read_at">
                      {{ formatReadAt(person.read_at) }}
                    </time>
                  </li>
                </ul>
              </section>
            </div>
          </template>

          <ErrorsMessages :errors="receiptsErrors" />
        </div>
      </b-overlay>

      <template #modal-footer>
        <button
          type="button"
          class="ch-btn ch-btn--secondary ch-receipts__refresh"
          :disabled="receiptsLoading || !receiptsMessageId"
          @click="loadReceipts"
        >
          <i class="fas fa-sync-alt" aria-hidden="true"></i>
          Atualizar
        </button>

        <button
          type="button"
          class="ch-btn ch-btn--secondary"
          @click="receiptsOpen = false"
        >
          Fechar
        </button>

        <!-- Ação de acompanhamento: cobrar só quem falta, sem redigitar -->
        <button
          v-if="receipts && receipts.total - receipts.read_count > 0"
          type="button"
          class="ch-btn ch-btn--primary"
          @click="remindPending"
        >
          <i class="fas fa-bell" aria-hidden="true"></i>
          Lembrar quem não leu ({{ receipts.total - receipts.read_count }})
        </button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";

import NavBar from "../templates/NavBar";
import AuthBySessionExpired from "../templates/Components/AuthBySessionExpired";
import ErrorsMessages from "../templates/Components/ErrorsMessages";

import api from "../services/api";
import chatService from "../services/chatService";
import toast from "../lib/ToastNotification";

const WEEKDAYS = [
  "domingo",
  "segunda-feira",
  "terça-feira",
  "quarta-feira",
  "quinta-feira",
  "sexta-feira",
  "sábado",
];

const SEARCH_DEBOUNCE_MS = 400;
const LONG_BODY_CHARS = 280;
const LONG_BODY_LINES = 4;

function emptyFilters() {
  return { person_id: "", status: "", date_init: "", date_end: "" };
}

function normalize(text) {
  return String(text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

export default {
  name: "ChatHistory",

  components: { NavBar, AuthBySessionExpired, ErrorsMessages },

  data() {
    return {
      chat: chatService.state,

      tabs: [
        { value: "RECEIVED", label: "Recebidas", icon: "fa-inbox" },
        { value: "SENT", label: "Enviadas", icon: "fa-paper-plane" },
      ],

      box:
        this.$route && this.$route.query.aba === "enviadas"
          ? "SENT"
          : "RECEIVED",
      page: 1,
      perPage: 20,
      total: 0,
      unreadTotal: 0,
      messages: [],

      searchInput: "",
      search: "",
      searchTimer: null,
      filters: emptyFilters(),

      expanded: {},
      markingId: null,

      loading: false,
      requestId: 0,
      errors: [],

      // Modal "quem leu"
      receiptsOpen: false,
      receiptsMessageId: null,
      receipts: null,
      receiptsLoading: false,
      receiptsErrors: [],
      receiptsSearch: "",
    };
  },

  computed: {
    online() {
      return !this.$store || this.$store.state.online !== false;
    },

    hasFilters() {
      const f = this.filters;
      return !!(
        this.search ||
        f.person_id ||
        f.status ||
        f.date_init ||
        f.date_end
      );
    },

    groupedMessages() {
      const groups = [];
      const index = {};
      this.messages.forEach((message) => {
        const day = moment(message.created_at);
        const key = day.isValid() ? day.format("YYYY-MM-DD") : "sem-data";
        if (!index[key]) {
          index[key] = { key, label: this.dayLabel(day), items: [] };
          groups.push(index[key]);
        }
        index[key].items.push(message);
      });
      return groups;
    },

    filteredReceipts() {
      if (!this.receipts) return [];
      const term = normalize(this.receiptsSearch.trim());
      if (!term) return this.receipts.recipients;
      return this.receipts.recipients.filter(
        (p) =>
          normalize(p.name).includes(term) ||
          normalize(p.user_group_name).includes(term)
      );
    },

    readRecipients() {
      return this.filteredReceipts.filter((p) => p.read_at);
    },

    pendingRecipients() {
      return this.filteredReceipts.filter((p) => !p.read_at);
    },
  },

  watch: {
    searchInput(value) {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.search = value.trim();
      }, SEARCH_DEBOUNCE_MS);
    },

    search() {
      this.reload();
    },

    filters: {
      deep: true,
      handler() {
        this.reload();
      },
    },

    page() {
      this.fetch();
      this.$nextTick(() => {
        const panel = this.$refs.panel;
        if (panel && panel.scrollIntoView)
          panel.scrollIntoView({ block: "start" });
      });
    },

    // Chegou, leu ou enviou em qualquer lugar do sistema: atualiza em silêncio
    "chat.version"() {
      this.fetch({ silent: true });
      if (this.receiptsOpen && this.receiptsMessageId)
        this.loadReceipts({ silent: true });
    },

    online(isOnline) {
      if (isOnline) this.fetch();
    },
  },

  created() {
    chatService.fetchRecipients();
    if (this.online) this.fetch();
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },

  methods: {
    handleToBack() {
      this.$router.go(-1);
    },

    // ── Abas ─────────────────────────────────────────────────────────
    setBox(box) {
      if (this.box === box) return;
      this.box = box;
      this.filters.status = ""; // opções de leitura mudam por aba
      this.filters.person_id = "";
      this.expanded = {};

      const aba = box === "SENT" ? "enviadas" : "recebidas";
      if (this.$route && this.$route.query.aba !== aba) {
        this.$router
          .replace({ query: { ...this.$route.query, aba } })
          .catch(() => {});
      }
      this.reload();
    },

    switchTabByKey() {
      const next = this.box === "RECEIVED" ? "SENT" : "RECEIVED";
      this.setBox(next);
      this.$nextTick(() => {
        const btn = document.getElementById(`chat-tab-${next}`);
        if (btn) btn.focus();
      });
    },

    clearFilters() {
      clearTimeout(this.searchTimer);
      this.searchInput = "";
      this.search = "";
      this.filters = emptyFilters();
    },

    // ── Dados ────────────────────────────────────────────────────────
    reload() {
      if (this.page !== 1) {
        this.page = 1; // o watcher de page dispara o fetch
        return;
      }
      this.fetch();
    },

    async fetch({ silent = false } = {}) {
      if (!this.online) return;

      // Evita que uma resposta antiga sobrescreva uma mais nova
      const requestId = ++this.requestId;

      try {
        if (!silent) this.loading = true;

        const params = {
          box: this.box,
          page: this.page,
          search: this.search || undefined,
          person_id: this.filters.person_id || undefined,
          status: this.filters.status || undefined,
          date_init: this.filters.date_init || undefined,
          date_end: this.filters.date_end || undefined,
        };

        const response = await api.get("/rest-home/chat/history", { params });
        if (requestId !== this.requestId) return;

        const { error, errors, messages, total, per_page, unread_total } =
          response.data || {};

        if (error) {
          this.errors = errors || ["NÃO FOI POSSÍVEL CARREGAR O HISTÓRICO"];
          return;
        }

        this.errors = [];
        this.messages = messages || [];
        this.total = total || 0;
        this.perPage = per_page || this.perPage;
        this.unreadTotal = unread_total || 0;
      } catch (error) {
        console.log("ChatHistory.fetch:", error);
        if (requestId === this.requestId) {
          this.errors = ["FALHA DE CONEXÃO AO CARREGAR O HISTÓRICO"];
        }
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },

    // ── Ações das recebidas ──────────────────────────────────────────
    async markAsRead(message) {
      this.markingId = message.id;
      const ok = await chatService.markAsRead([message.id]);
      this.markingId = null;

      if (ok) {
        // Retorno imediato; o fetch silencioso confirma depois
        message.read_at = moment().format("YYYY-MM-DD HH:mm:ss");
        this.unreadTotal = Math.max(this.unreadTotal - 1, 0);
      } else {
        toast("NÃO FOI POSSÍVEL MARCAR COMO LIDA", "error");
      }
    },

    reply(message) {
      chatService.openComposer({ replyTo: message });
    },

    newMessage() {
      chatService.openComposer();
    },

    // ── Modal "quem leu" ─────────────────────────────────────────────
    openReceipts(message) {
      this.receiptsMessageId = message.id;
      this.receipts = null;
      this.receiptsErrors = [];
      this.receiptsSearch = "";
      this.receiptsOpen = true;
      this.loadReceipts();
    },

    async loadReceipts({ silent = false } = {}) {
      const id = this.receiptsMessageId;
      if (!id) return;

      try {
        if (!silent) this.receiptsLoading = true;
        const response = await api.get("/rest-home/chat/message/receipts", {
          params: { id },
        });
        if (id !== this.receiptsMessageId) return;

        const { error, errors, message, recipients, read_count, total } =
          response.data || {};
        if (error) {
          this.receiptsErrors = errors || [
            "NÃO FOI POSSÍVEL CARREGAR AS LEITURAS",
          ];
          return;
        }
        this.receiptsErrors = [];
        this.receipts = {
          message,
          recipients: recipients || [],
          read_count,
          total,
        };

        // Mantém o card da lista coerente com o modal
        const card = this.messages.find((m) => m.id === id);
        if (card) {
          card.recipients_read = read_count;
          card.recipients_total = total;
        }
      } catch (error) {
        console.log("ChatHistory.loadReceipts:", error);
        this.receiptsErrors = ["FALHA DE CONEXÃO AO CARREGAR AS LEITURAS"];
      } finally {
        this.receiptsLoading = false;
      }
    },

    remindPending() {
      if (!this.receipts) return;
      const { message, recipients } = this.receipts;
      const recipientIds = recipients
        .filter((p) => !p.read_at)
        .map((p) => p.id);
      const sentAt = moment(message.created_at).format("DD/MM [às] HH:mm");
      const body = `Lembrete da mensagem de ${sentAt}:\n"${this.excerpt(
        message.body,
        300
      )}"`;

      this.receiptsOpen = false;
      // Espera o modal fechar para o compositor receber o foco
      setTimeout(
        () => chatService.openComposer({ preset: { recipientIds, body } }),
        250
      );
    },

    // ── Formatação ───────────────────────────────────────────────────
    dayLabel(day) {
      if (!day.isValid()) return "Sem data";
      if (day.isSame(moment(), "day")) return "Hoje";
      if (day.isSame(moment().subtract(1, "day"), "day")) return "Ontem";
      return `${WEEKDAYS[day.day()]}, ${day.format("DD/MM/YYYY")}`;
    },

    formatTime(date) {
      const m = moment(date);
      return m.isValid() ? m.format("HH:mm") : "";
    },

    formatReadAt(date) {
      const m = moment(date);
      if (!m.isValid()) return "";
      if (m.isSame(moment(), "day")) return `hoje às ${m.format("HH:mm")}`;
      if (m.isSame(moment().subtract(1, "day"), "day"))
        return `ontem às ${m.format("HH:mm")}`;
      return `em ${m.format("DD/MM/YYYY [às] HH:mm")}`;
    },

    excerpt(text, max = 120) {
      const clean = String(text || "")
        .replace(/\s+/g, " ")
        .trim();
      return clean.length > max ? `${clean.slice(0, max)}…` : clean;
    },

    initials(name) {
      const parts = String(name || "?")
        .trim()
        .split(/\s+/);
      const first = parts[0] ? parts[0][0] : "?";
      const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
      return (first + last).toUpperCase();
    },

    cardPerson(message) {
      if (this.box === "RECEIVED") return message.sender_name;
      return (message.recipients_preview || [])[0] || "?";
    },

    sentToLabel(message) {
      if (message.is_broadcast) return "toda a equipe";
      const names = message.recipients_preview || [];
      const rest = message.recipients_total - names.length;
      if (!names.length) return `${message.recipients_total} pessoas`;
      return rest > 0 ? `${names.join(", ")} e mais ${rest}` : names.join(", ");
    },

    destinationTag(message) {
      if (message.is_broadcast) return "Para todos";
      return this.box === "RECEIVED" ? "Para você" : "Individual";
    },

    isLong(message) {
      const body = String(message.body || "");
      return (
        body.length > LONG_BODY_CHARS ||
        body.split("\n").length > LONG_BODY_LINES
      );
    },

    toggleExpanded(id) {
      this.$set(this.expanded, id, !this.expanded[id]);
    },

    // ── Estado de leitura das enviadas ───────────────────────────────
    readState(message) {
      const { recipients_read: read, recipients_total: total } = message;
      if (total > 0 && read >= total) return "all";
      if (read > 0) return "partial";
      return "none";
    },

    readLabel(message) {
      const { recipients_read: read, recipients_total: total } = message;
      if (total === 1) return read ? "Lida" : "Ainda não lida";
      if (total > 0 && read >= total) return `Todos leram (${total})`;
      if (read === 0) return `Ninguém leu ainda (0 de ${total})`;
      return `Lida por ${read} de ${total}`;
    },

    readIcon(message) {
      return {
        all: "fa-check-double",
        partial: "fa-check",
        none: "fa-clock",
      }[this.readState(message)];
    },

    readPercent(message) {
      const { recipients_read: read, recipients_total: total } = message;
      return total ? Math.round((read / total) * 100) : 0;
    },
  },
};
</script>

<style scoped>
/*
  Tokens locais. Seguem a paleta da tela de Plantões (Duty.vue).
  Não alteram os CSS globais.
*/
.chat-history {
  --ch-primary: #0f65b0;
  --ch-primary-dark: #0b4f8a;
  --ch-primary-soft: #eff6ff;
  --ch-text: #1e293b;
  --ch-text-muted: #475569; /* 7.5:1 sobre branco */
  --ch-border: #cbd5e1;
  --ch-border-soft: #e2e8f0;
  --ch-surface: #ffffff;
  --ch-surface-alt: #f8fafc;
  --ch-unread: #c2410c; /* mesmo laranja do contador do sininho */
  --ch-unread-soft: #fff7ed;
  --ch-success: #15803d;
  --ch-success-soft: #dcfce7;
  --ch-warning-text: #78350f;
  --ch-warning-soft: #fef3c7;

  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  color: var(--ch-text);
}

.chat-history__container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px 40px;
}

/* ── Cabeçalho ───────────────────────────────────────────────────────── */
.chat-history__header {
  margin-bottom: 24px;
  padding: 24px 0;
  border-radius: 0 0 24px 24px;
  background: var(--ch-surface);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.chat-history__header-content {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 24px;
  padding: 0 24px;
}

.chat-history__title {
  display: flex;
  align-items: center;
  margin: 0;
  font-size: 30px;
  font-weight: 700;
  color: var(--ch-text);
}

.chat-history__title-icon {
  margin-right: 12px;
  color: var(--ch-primary);
}

.chat-history__subtitle {
  margin: 4px 0 0;
  font-size: 16px;
  color: var(--ch-text-muted);
}

.chat-history__offline {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border: 1px solid #fca5a5;
  border-radius: 12px;
  background: #fef2f2;
  color: #991b1b;
  font-weight: 600;
}

/* ── Abas ────────────────────────────────────────────────────────────── */
.chat-history__tabs {
  display: inline-flex;
  gap: 4px;
  margin-bottom: 16px;
  padding: 4px;
  border-radius: 14px;
  background: var(--ch-border-soft);
}

.chat-history__tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 20px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--ch-text-muted);
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
}

.chat-history__tab:hover {
  color: var(--ch-text);
}

.chat-history__tab--active {
  background: var(--ch-surface);
  color: var(--ch-primary);
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.12);
}

.chat-history__tab-badge {
  min-width: 22px;
  padding: 0 7px;
  border-radius: 999px;
  background: var(--ch-unread);
  color: #ffffff;
  font-size: 12px;
  line-height: 22px;
  text-align: center;
}

/* ── Filtros ─────────────────────────────────────────────────────────── */
.chat-history__filters {
  display: grid;
  grid-template-columns: 2fr 1.4fr 1.2fr 1fr 1fr;
  gap: 16px;
  padding: 20px;
  border-radius: 16px;
  background: var(--ch-surface);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
}

.chat-history__filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.chat-history__filter-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.chat-history__filter-label i {
  color: #6b7280;
}

.ch-input {
  width: 100%;
  height: 44px;
  padding: 0 12px;
  border: 1px solid var(--ch-border);
  border-radius: 10px;
  background: var(--ch-surface);
  color: var(--ch-text);
  font-size: 16px;
}

.ch-input:focus {
  border-color: var(--ch-primary);
  outline: 3px solid rgba(15, 101, 176, 0.25);
}

/* ── Resumo ──────────────────────────────────────────────────────────── */
.chat-history__summary {
  display: flex;
  align-items: center;
  gap: 16px;
  min-height: 44px;
  margin: 12px 0 4px;
}

.chat-history__count {
  margin: 0;
  font-size: 15px;
  color: var(--ch-text-muted);
}

/* ── Lista ───────────────────────────────────────────────────────────── */
.chat-history__panel {
  min-height: 160px;
}

.chat-history__day {
  margin-bottom: 20px;
}

/* Separador de dia "gruda" no topo ao rolar: o contexto não se perde */
.chat-history__day-label {
  position: sticky;
  top: 0;
  z-index: 2;
  margin: 0 0 8px;
  padding: 8px 4px;
  background: #eef2f7;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--ch-text-muted);
}

.chat-history__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ch-card {
  display: flex;
  gap: 14px;
  padding: 16px;
  border: 1px solid var(--ch-border-soft);
  border-left: 4px solid transparent;
  border-radius: 14px;
  background: var(--ch-surface);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
}

/* Não lida: borda + fundo + selo de texto (nunca só cor) */
.ch-card--unread {
  border-left-color: var(--ch-unread);
  background: var(--ch-unread-soft);
}

.ch-card__avatar {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0f65b0 0%, #1e40af 100%);
  color: #ffffff;
  font-size: 15px;
  font-weight: 700;
}

.ch-card__content {
  flex: 1;
  min-width: 0;
}

.ch-card__top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.ch-card__person {
  font-size: 16px;
  font-weight: 700;
  color: var(--ch-text);
  overflow-wrap: anywhere;
}

.ch-card__to {
  font-weight: 500;
  color: var(--ch-text-muted);
}

.ch-card__time {
  margin-left: auto;
  font-size: 14px;
  font-weight: 600;
  color: var(--ch-text-muted);
}

.ch-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.ch-tag--me {
  background: #dbeafe;
  color: #1e3a8a;
}

.ch-tag--all {
  background: var(--ch-warning-soft);
  color: var(--ch-warning-text);
}

.ch-card__body {
  margin: 0 0 4px;
  font-size: 16px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.ch-card__body--clamped {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.ch-card__bottom {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  margin-top: 10px;
}

.ch-card__voice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--ch-text-muted);
}

.ch-card__actions {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-left: auto;
}

/* ── Status de leitura ───────────────────────────────────────────────── */
.ch-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
}

.ch-status--read,
.ch-status--all {
  color: var(--ch-success);
}

.ch-status--unread {
  color: var(--ch-unread);
}

.ch-status--unread i {
  font-size: 9px;
}

.ch-status--partial {
  color: var(--ch-primary);
}

.ch-status--none {
  color: var(--ch-text-muted);
}

.ch-progress {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 200px;
}

.ch-progress__bar {
  display: block;
  width: 100%;
  max-width: 260px;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--ch-border-soft);
}

.ch-progress__fill {
  display: block;
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s ease;
}

.ch-progress__fill--all {
  background: var(--ch-success);
}

.ch-progress__fill--partial {
  background: var(--ch-primary);
}

.ch-progress__fill--none {
  background: transparent;
}

/* ── Vazio / paginação ───────────────────────────────────────────────── */
.chat-history__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 16px;
  border-radius: 16px;
  background: var(--ch-surface);
  color: var(--ch-text-muted);
  text-align: center;
}

.chat-history__empty i {
  font-size: 40px;
  color: #94a3b8;
}

.chat-history__empty p {
  margin: 0;
  font-size: 17px;
}

.chat-history__pagination {
  margin-top: 16px;
}

/* ── Botões ──────────────────────────────────────────────────────────── */
.ch-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 20px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
}

.ch-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ch-btn--small {
  min-height: 40px;
  padding: 0 14px;
  font-size: 14px;
}

.ch-btn--primary {
  border: 0;
  background: var(--ch-primary, #0f65b0);
  color: #ffffff;
}

.ch-btn--primary:hover:not(:disabled) {
  background: var(--ch-primary-dark, #0b4f8a);
}

.ch-btn--secondary {
  border: 1px solid var(--ch-border, #cbd5e1);
  background: #ffffff;
  color: var(--ch-text, #1e293b);
}

.ch-btn--secondary:hover:not(:disabled) {
  background: #f8fafc;
}

.ch-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  color: var(--ch-primary, #0f65b0);
  font-size: 15px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

.ch-link-btn--small {
  min-height: 32px;
  font-size: 14px;
}

button:focus {
  outline: none;
}

button:focus-visible {
  outline: 3px solid rgba(15, 101, 176, 0.55);
  outline-offset: 2px;
}

.ch-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ── Modal "quem leu" ────────────────────────────────────────────────── */
.ch-receipts__title {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 19px;
  font-weight: 700;
  color: #fff;
}

.ch-receipts__title i {
  color: #0f65b0;
}

.ch-receipts {
  color: #1e293b;
  font-size: 16px;
}

.ch-receipts__quote {
  margin: 0 0 16px;
  padding: 12px 14px;
  border-left: 4px solid #0f65b0;
  border-radius: 8px;
  background: #eff6ff;
}

.ch-receipts__quote p {
  margin: 0 0 6px;
  white-space: pre-wrap;
}

.ch-receipts__quote footer {
  font-size: 14px;
  color: #475569;
}

.ch-receipts__totals {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.ch-receipts__total {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 14px;
  border-radius: 12px;
}

.ch-receipts__total strong {
  font-size: 32px;
  line-height: 1.1;
}

.ch-receipts__total span {
  font-size: 15px;
  font-weight: 600;
}

.ch-receipts__total--read {
  background: #dcfce7;
  color: #14532d;
}

.ch-receipts__total--pending {
  background: #fff7ed;
  color: #7c2d12;
}

.ch-receipts__search {
  position: relative;
  margin-bottom: 12px;
}

.ch-receipts__search i {
  position: absolute;
  top: 50%;
  left: 14px;
  color: #475569;
  transform: translateY(-50%);
  pointer-events: none;
}

.ch-receipts__search .ch-input {
  padding-left: 40px;
  border: 1px solid #cbd5e1;
}

.ch-receipts__columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.ch-receipts__column-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.ch-receipts__none {
  margin: 0;
  padding: 12px;
  border-radius: 8px;
  background: #f8fafc;
  color: #475569;
  font-size: 14px;
}

.ch-receipts__people {
  max-height: 320px;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  list-style: none;
}

.ch-receipts__person {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
}

.ch-receipts__person:last-child {
  border-bottom: 0;
}

.ch-receipts__name {
  font-weight: 600;
}

.ch-receipts__group {
  font-size: 13px;
  color: #475569;
}

.ch-receipts__when {
  width: 100%;
  font-size: 13px;
  color: #15803d;
}

.ch-receipts__refresh {
  margin-right: auto;
}

/* ── Responsivo ──────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .chat-history__header-content {
    grid-template-columns: 1fr;
    gap: 16px;
    text-align: center;
  }

  .chat-history__title {
    justify-content: center;
  }

  .chat-history__filters {
    grid-template-columns: 1fr 1fr;
  }

  .chat-history__filter--search {
    grid-column: 1 / -1;
  }
}

@media (max-width: 576px) {
  .chat-history__container {
    padding: 0 12px 32px;
  }

  .chat-history__title {
    font-size: 22px;
  }

  .chat-history__tabs {
    display: flex;
  }

  .chat-history__tab {
    flex: 1;
    justify-content: center;
    padding: 0 10px;
  }

  .chat-history__filters {
    grid-template-columns: 1fr;
    padding: 16px;
  }

  .ch-card {
    padding: 14px 12px;
  }

  .ch-card__avatar {
    display: none; /* ganha largura para o texto no celular */
  }

  .ch-card__actions {
    width: 100%;
    margin-left: 0;
  }

  .ch-card__actions .ch-btn {
    flex: 1;
  }

  .ch-receipts__columns {
    grid-template-columns: 1fr;
  }
}
</style>